// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { getCurrencySymbol } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import type {
  EditProductPriceMutation,
  EditProductPriceMutationVariables,
  ProductDataQuery,
  ProductDataQueryVariables,
} from '../../types/__generated__/graphql';
import { createID, getNumericID, validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';
import SelectOrdersContainer from './SelectOrdersContainer';
import SelectRecurringOrdersContainer from './SelectRecurringOrdersContainer';

const EDIT_PRODUCT_MUT = gql`
  mutation EditProductPrice($product_id: Int!, $new_price: Float, $orders_to_update: [Int!]!, $recurring_orders_to_update: [Int!]!) {
    editProductPrice(input: { product_id: $product_id, new_price: $new_price, orders_to_update: $orders_to_update, recurring_orders_to_update: $recurring_orders_to_update }) {
      updated_crop {
        id
        updated_at
      }
      updated_blend {
        id
        updated_at
      }
      updated_product {
        id
        price_per_item
        updated_at
      }
      updated_orders {
        id
        updated_at
      }
      updated_recurring_orders {
        id
        updated_at
      }
      updated_order_inputs {
        id
        price_per_item
        percent_discount_at_creation
      }
    }
  }
`;

const PRODUCT_DATA_QUERY = gql`
  query ProductData($product_id: ID!) {
    container_product(id: $product_id) {
      id
      size
      unit
      price_per_item
      orders(filters: [
        { name: "order.order_status", operator: "=", value: "in_progress" }
      ]) {
        edges {
          node {
            id
            customer
            order_fulfillment_time
            inputs {
              edges {
                node {
                  id
                  price_per_item
                  percent_discount_at_creation
                  product_id
                  size_amount
                  quantity
                  unit
                }
              }
            }
          }
        }
      }
      recurring_orders {
        edges {
          node {
            id
            customer
            recurring_start_time
            recurring_end_time
            status
            inputs {
              edges {
                node {
                  id
                  price_per_item
                  percent_discount_at_creation
                  product_id
                  size_amount
                  quantity
                  unit
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly productId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

// Helper to filter out duplicate nodes by id
function filterUniqueById<T extends { id: string }>(arr: T[]): T[] {
  const seen = new Set<string>();
  return arr.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function parseOrderInput(input: {
  id: string;
  price_per_item?: string | null;
  percent_discount_at_creation?: string | null;
  product_id?: string | null;
  size_amount?: string | null;
  quantity: string;
  unit: string;
}) {
  return {
    id: getNumericID(input.id),
    // Parsing int should not be needed, but for some reason the product_id is a string in the GraphQL response
    product_id: input.product_id ? Number.parseInt(input.product_id, 10) : undefined,
    price_per_item: input.price_per_item ? Number.parseFloat(input.price_per_item) : undefined,
    percent_discount_at_creation: input.percent_discount_at_creation
      ? Number.parseFloat(input.percent_discount_at_creation)
      : undefined,
    size_amount: input.size_amount ?? undefined,
    quantity: input.quantity,
    unit: input.unit,
  };
}

export default function EditProductPriceDialog({ isOpen, onClose, onSuccess, productId }: Props) {
  const [createProduct, { loading }] = useMutation<EditProductPriceMutation, EditProductPriceMutationVariables>(
    EDIT_PRODUCT_MUT,
  );
  const [getProductData, { data, loading: loadingProductData }] = useLazyQuery<
    ProductDataQuery,
    ProductDataQueryVariables
  >(PRODUCT_DATA_QUERY, {
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    if (isOpen) {
      getProductData({
        variables: {
          product_id: createID.product(productId),
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.container_product != null) {
            setPrice(dataIn.container_product.price_per_item ?? '');
            setSelectedOrders(dataIn.container_product.orders.edges.map((node) => getNumericID(node.node.id)));
            setSelectedRecurringOrders(
              dataIn.container_product.recurring_orders.edges.map((node) => getNumericID(node.node.id)),
            );
          } else {
            enqueueSnackbar('Error loading product data, no data returned', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          clientLogger.error({ title: 'edit_product_error', message: errorIn.message });
        },
      });
    }
  }, [isOpen, getProductData, productId]);

  const ordersRaw = (data?.container_product?.orders?.edges ?? []).map((edge) => edge.node);
  const recurringOrdersRaw = (data?.container_product?.recurring_orders?.edges ?? []).map((edge) => edge.node);

  const orders = filterUniqueById(ordersRaw).map((order) => ({
    ...order,
    id: getNumericID(order.id),
    inputs: order.inputs.edges
      .map((inputEdge) => parseOrderInput(inputEdge.node))
      .filter(({ product_id }) => product_id === productId),
  }));
  const recurringOrders = filterUniqueById(recurringOrdersRaw).map((order) => ({
    ...order,
    id: getNumericID(order.id),
    inputs: order.inputs.edges
      .map((inputEdge) => parseOrderInput(inputEdge.node))
      .filter(({ product_id }) => product_id === productId),
  }));

  const { currencyCode, timezone } = useUserContext();
  const currencySymbol = getCurrencySymbol(currencyCode);

  const [price, setPrice] = React.useState('');
  const [priceValidation, setPriceValidation] = React.useState('');
  const [selectedOrders, setSelectedOrders] = React.useState<number[]>([]);
  const [selectedRecurringOrders, setSelectedRecurringOrders] = React.useState<number[]>([]);
  const [expandedOrderIds, setExpandedOrderIds] = React.useState<Set<number>>(new Set());
  const [expandedRecurringOrderIds, setExpandedRecurringOrderIds] = React.useState<Set<number>>(new Set());

  const onSetSelectedOrders = (orders: number[]) => {
    setSelectedOrders(orders);
  };
  const onSetSelectedRecurringOrders = (orders: number[]) => {
    setSelectedRecurringOrders(orders);
  };
  const onSelectAllOrders = () => {
    setSelectedOrders(orders.map(({ id }) => id));
    setSelectedRecurringOrders(recurringOrders.map(({ id }) => id));
  };
  const onClearSelectedOrders = () => {
    setSelectedOrders([]);
    setSelectedRecurringOrders([]);
  };
  const onPriceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => setPriceValidation(msg),
    });
    setPrice(e.target.value);
  };
  const onPriceBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => setPriceValidation(msg),
    });
  };

  const expandAll = () => {
    setExpandedOrderIds(new Set(orders.map((order) => order.id)));
    setExpandedRecurringOrderIds(new Set(recurringOrders.map((order) => order.id)));
  };
  const collapseAll = () => {
    setExpandedOrderIds(new Set());
    setExpandedRecurringOrderIds(new Set());
  };

  const disableFields = loading || loadingProductData;

  const onSubmit = () => {
    void createProduct({
      variables: {
        product_id: productId,
        new_price: price === '' ? null : Number.parseFloat(price),
        orders_to_update: selectedOrders,
        recurring_orders_to_update: selectedRecurringOrders,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editProductPrice != null) {
          enqueueSnackbar('Updated product', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error updated product, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setPrice('');
        setPriceValidation('');
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_product_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = priceValidation !== '';
  const isSubmitDisabled: boolean = hasInputValidationError || disableFields;

  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const productName =
    data?.container_product == null ? undefined : `${data.container_product.size} ${data.container_product.unit}`;

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{`Edit Product Price${productName ? ` - ${productName}` : ''}`}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            aria-describedby="new price"
            id="price"
            label="New Price"
            value={price}
            onChange={onPriceChange}
            onBlur={onPriceBlur}
            startAdornment={<span style={{ marginRight: 4 }}>{currencySymbol}</span>}
            hasSubtext
            subtext={priceValidation}
            error={priceValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            numberStep={0.01}
            disabled={disableFields}
          />
          <Typography variant="subtitle1">
            {`This will also update the price of this product to ${currencySymbol}${price} for all your planned orders${orders.length + recurringOrders.length > 0 ? ', and for the selected recurring orders and in progress orders below' : ''}. Even if an order has a different price for this product (e.g., a discount), it will be updated to the new price.`}
          </Typography>
          {orders.length + recurringOrders.length > 0 && (
            <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                onClick={onSelectAllOrders}
                disabled={disableFields}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                onClick={onClearSelectedOrders}
                disabled={disableFields}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Clear All
              </Button>
              <Button variant="outlined" onClick={expandAll} disabled={disableFields} sx={{ whiteSpace: 'nowrap' }}>
                Expand All
              </Button>
              <Button variant="outlined" onClick={collapseAll} disabled={disableFields} sx={{ whiteSpace: 'nowrap' }}>
                Collapse All
              </Button>
            </Box>
          )}
          {orders.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Select in progress orders to update
              </Typography>
              <SelectOrdersContainer
                orders={orders}
                timezone={timezone}
                selectedOrders={selectedOrders}
                setSelectedOrders={onSetSelectedOrders}
                expandedOrderIds={expandedOrderIds}
                setExpandedOrderIds={setExpandedOrderIds}
              />
            </Box>
          )}
          {recurringOrders.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Select recurring orders to update
              </Typography>
              <SelectRecurringOrdersContainer
                orders={recurringOrders}
                selectedOrders={selectedRecurringOrders}
                setSelectedOrders={onSetSelectedRecurringOrders}
                expandedOrderIds={expandedRecurringOrderIds}
                setExpandedOrderIds={setExpandedRecurringOrderIds}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
